import { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dashboardAPI, getThumbnailUrl } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import { useAuthStore } from '../stores/authStore';
import { getDualTimeInfo } from '../utils/formatDateTime';
import { useSocketStore } from '../stores/socketStore';
import { AlertThumbnail } from '../components/ImageModal';
import {
  UsersIcon,
  ShieldCheckIcon,
  BellAlertIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  FireIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import clsx from 'clsx';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Animated counter hook
function useAnimatedCounter(endValue, duration = 1000) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (typeof endValue !== 'number') return;

    let startTime;
    const startValue = 0;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(startValue + (endValue - startValue) * easeOutQuart));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [endValue, duration]);

  return count;
}

const DATE_RANGE_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: '7 Days', value: '7d' },
  { label: '30 Days', value: '30d' },
  { label: 'Custom', value: 'custom' },
];

function getDateRange(preset) {
  // Use local date, not UTC - important for timezone correctness
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const date = String(now.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${date}`;

  switch (preset) {
    case 'today': {
      // For today: pass the same date for both start and end
      return { startDate: todayStr, endDate: todayStr };
    }
    case '7d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      const startYear = start.getFullYear();
      const startMonth = String(start.getMonth() + 1).padStart(2, '0');
      const startDate = String(start.getDate()).padStart(2, '0');
      const startStr = `${startYear}-${startMonth}-${startDate}`;
      return { startDate: startStr, endDate: todayStr };
    }
    case '30d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      const startYear = start.getFullYear();
      const startMonth = String(start.getMonth() + 1).padStart(2, '0');
      const startDate = String(start.getDate()).padStart(2, '0');
      const startStr = `${startYear}-${startMonth}-${startDate}`;
      return { startDate: startStr, endDate: todayStr };
    }
    default:
      return null;
  }
}

export default function DashboardPage() {
  const { selectedSite } = useSiteStore();
  const { subscribeSite, setActiveAlerts } = useSocketStore();
  const { refreshUser, user } = useAuthStore();
  const orgTimezone = user?.organization?.timezone;
  const [datePreset, setDatePreset] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Refresh user profile on mount to keep org timezone up-to-date
  useEffect(() => { refreshUser(); }, []);

  // Live clock that ticks every 30s (syncs with data refetch)
  const [currentTime, setCurrentTime] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when orgTimezone updates from refreshUser
  const dualTime = useMemo(() => getDualTimeInfo(currentTime), [currentTime, orgTimezone]);

  const dateRange = useMemo(() => {
    if (datePreset === 'custom' && customStart) {
      return { startDate: customStart, endDate: customEnd || customStart };
    }
    return getDateRange(datePreset);
  }, [datePreset, customStart, customEnd]);

  const isToday = datePreset === 'today';

  // Subscribe to site updates
  useEffect(() => {
    if (selectedSite?.id) {
      subscribeSite(selectedSite.id);
    }
  }, [selectedSite?.id]);

  // Fetch dashboard data
  const { data: summary, isLoading, isFetching } = useQuery({
    queryKey: ['dashboard-summary', selectedSite?.id, dateRange],
    queryFn: () => dashboardAPI.getSummary(selectedSite?.id, dateRange),
    refetchInterval: isToday ? 30000 : false,
  });

  const { data: hourlyCompliance } = useQuery({
    queryKey: ['hourly-compliance', selectedSite?.id, dateRange?.startDate],
    queryFn: () => dashboardAPI.getHourlyCompliance(selectedSite?.id, dateRange?.startDate),
    refetchInterval: isToday ? 60000 : false,
  });

  const daysInRange = useMemo(() => {
    if (!dateRange) return 7;
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
  }, [dateRange]);

  const { data: dailyStats } = useQuery({
    queryKey: ['daily-stats', selectedSite?.id, dateRange],
    queryFn: () => dashboardAPI.getDailyStats(selectedSite?.id, daysInRange, dateRange),
    refetchInterval: isToday ? 300000 : false,
  });

  // Alerts are pushed via WebSocket (alert:new event), no need for aggressive polling
  // Only fetch on mount/manual refresh, WebSocket handles real-time updates
  const { data: liveAlerts } = useQuery({
    queryKey: ['live-alerts', selectedSite?.id],
    queryFn: () => dashboardAPI.getActiveAlerts(selectedSite?.id),
    refetchInterval: false, // Disabled - WebSocket pushes updates
  });

  // Sync active alerts count when liveAlerts data changes (onSuccess removed in React Query v5)
  useEffect(() => {
    if (liveAlerts?.data) {
      setActiveAlerts(Array.isArray(liveAlerts.data) ? liveAlerts.data.length : 0);
    }
  }, [liveAlerts?.data]);

  // Update active alerts count
  useEffect(() => {
    if (summary?.data?.alerts?.active !== undefined) {
      setActiveAlerts(summary.data.alerts.active);
    }
  }, [summary?.data?.alerts?.active]);

  const data = summary?.data;
  const hasData = !!data;
  const alerts = liveAlerts?.data || [];

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Safety Command Center</h1>
          <p className="page-description">
            {selectedSite ? selectedSite.name : 'All Sites'} • Real-time monitoring
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Date Range Picker */}
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-1 glass-card-glow px-2 py-1.5 rounded-xl">
              {DATE_RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDatePreset(opt.value)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                    datePreset === opt.value
                      ? 'text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100'
                  )}
                  style={datePreset === opt.value ? { background: 'var(--gradient-primary)' } : undefined}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {datePreset === 'custom' && (
              <div className="flex items-center gap-2 animate-fade-in">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="input-select text-xs px-2 py-1.5"
                  max={new Date().toISOString().split('T')[0]}
                />
                <span className="text-slate-400 text-xs">to</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="input-select text-xs px-2 py-1.5"
                  min={customStart}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}
          </div>
          {dualTime && (
            <div className="glass-card-glow px-4 py-2.5 flex items-center gap-3">
              <div className="relative">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping opacity-75"></div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-800">
                  {dualTime.siteTime} <span className="text-xs font-medium text-slate-500">{dualTime.siteLabel}</span>
                </p>
                {!dualTime.isSameZone && (
                  <p className="text-xs text-slate-400">
                    {dualTime.localTime} <span className="font-medium">{dualTime.localLabel}</span> (You)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hero Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Hero Card - PPE Safety */}
        <div className="hero-card lg:col-span-2">
          <div className="hero-card-content flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="hero-card-icon">
                  <ShieldCheckIcon className="w-7 h-7 text-white" />
                </div>
                {isToday && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                    </span>
                    <span className="text-white/90 text-xs font-semibold">Live</span>
                  </div>
                )}
              </div>
              <h3 className="text-white/80 text-sm font-medium mb-1">PPE Violations {isToday ? 'Today' : ''}</h3>
              {hasData && (data?.safety?.ppeViolations?.total || 0) === 0 ? (
                <>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-8 h-8 rounded-full bg-emerald-400/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="hero-card-value text-emerald-300">All Clear</p>
                  </div>
                  <p className="text-white/60 text-sm mt-2">No PPE violations detected — excellent compliance</p>
                </>
              ) : (
                <>
                  <p className="hero-card-value">{data?.safety?.ppeViolations?.total || 0}</p>
                  <p className="text-white/60 text-sm mt-2">
                    {`${data?.safety?.ppeViolations?.high || 0} High, ${data?.safety?.ppeViolations?.medium || 0} Medium severity`}
                  </p>
                </>
              )}
            </div>
            <div className="hidden sm:flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm">
                <UsersIcon className="w-4 h-4 text-white" />
                <span className="text-white font-semibold">{data?.workforce?.onSite || 0} on site</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm">
                <CalendarDaysIcon className="w-4 h-4 text-white" />
                <span className="text-white font-semibold">{data?.daysWithoutIncident || 0} days safe</span>
              </div>
            </div>
          </div>
        </div>

        {/* Alert Summary Card */}
        <div className={clsx(
          'glass-card-glow p-5 relative overflow-hidden',
          (data?.alerts?.active || 0) > 0 && 'ring-2 ring-red-200'
        )}>
          {(data?.alerts?.active || 0) > 0 && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-500"></div>
          )}
          <div className="flex items-start justify-between mb-4">
            <div className={clsx(
              'w-12 h-12 rounded-xl flex items-center justify-center',
              (data?.alerts?.active || 0) > 0
                ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-lg'
                : 'bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg'
            )} style={{ boxShadow: (data?.alerts?.active || 0) > 0 ? '0 4px 14px rgba(239, 68, 68, 0.4)' : '0 4px 14px rgba(16, 185, 129, 0.4)' }}>
              <BellAlertIcon className="w-6 h-6 text-white" />
            </div>
            {(data?.alerts?.active || 0) > 0 && (
              <span className="badge badge-solid-danger">Needs Attention</span>
            )}
          </div>
          <p className="text-sm text-slate-500 font-medium">Active Alerts</p>
          {hasData && (data?.alerts?.active || 0) === 0 ? (
            <p className="text-2xl font-bold text-emerald-600 mt-1">All Clear</p>
          ) : (
            <p className="text-4xl font-bold text-slate-800 mt-1">{data?.alerts?.active || 0}</p>
          )}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span className="text-xs text-slate-500">{data?.alerts?.bySeverity?.CRITICAL || 0} Critical</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-xs text-slate-500">{data?.alerts?.bySeverity?.HIGH || 0} High</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className={clsx(
        'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity duration-300',
        isFetching && !isLoading && 'opacity-60'
      )}>
        <MetricCard
          title="Workers On Site"
          value={data?.workforce?.onSite || 0}
          icon={UsersIcon}
          color="blue"
          delay={1}
        />
        <MetricCard
          title="PPE Violations"
          value={data?.safety?.ppeViolations?.total || 0}
          icon={ShieldCheckIcon}
          color={(data?.safety?.ppeViolations?.total || 0) === 0 ? 'green' : (data?.safety?.ppeViolations?.total || 0) <= 10 ? 'yellow' : 'red'}
          subtitle={hasData && (data?.safety?.ppeViolations?.total || 0) === 0 ? 'Fully compliant' : `${data?.safety?.ppeViolations?.high || 0} High severity`}
          delay={2}
        />
        <MetricCard
          title="Active Alerts"
          value={data?.alerts?.active || 0}
          icon={BellAlertIcon}
          color={data?.alerts?.active > 0 ? 'red' : 'green'}
          subtitle={hasData && (data?.alerts?.active || 0) === 0 ? 'No action needed' : `${data?.alerts?.bySeverity?.CRITICAL || 0} Critical`}
          pulse={data?.alerts?.active > 0}
          delay={3}
        />
        <MetricCard
          title="Days Without Incident"
          value={data?.daysWithoutIncident || 0}
          icon={CalendarDaysIcon}
          color="purple"
          subtitle={hasData && (data?.daysWithoutIncident || 0) > 0 ? 'Keep it up!' : undefined}
          delay={4}
        />
      </div>

      {/* Main Grid */}
      <div className={clsx(
        'grid grid-cols-1 lg:grid-cols-3 gap-6 transition-opacity duration-300',
        isFetching && !isLoading && 'opacity-60'
      )}>
        {/* Violations Trend Chart */}
        <div className="lg:col-span-2 glass-card-glow p-6 animate-slide-up stagger-2 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
          {/* Decorative gradient accent */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-red-500 to-rose-500"></div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg" style={{ boxShadow: '0 4px 14px rgba(249, 115, 22, 0.3)' }}>
                <ExclamationTriangleIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Violations {isToday ? 'Today' : dateRange?.startDate}</h3>
                <p className="text-xs text-slate-500">Hourly safety violations trend</p>
              </div>
            </div>
            <span className="badge badge-warning">24 Hours</span>
          </div>
          <ViolationsTrendChart data={hourlyCompliance?.data} />
        </div>

        {/* Active Alerts */}
        <div className="glass-card-glow p-6 animate-slide-up stagger-3 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
          {/* Decorative gradient accent */}
          <div className={clsx(
            'absolute top-0 left-0 right-0 h-1',
            alerts.length > 0 ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-emerald-500 to-teal-500'
          )}></div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={clsx(
                'w-10 h-10 rounded-xl flex items-center justify-center shadow-lg',
                alerts.length > 0
                  ? 'bg-gradient-to-br from-red-500 to-red-600'
                  : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
              )} style={{ boxShadow: alerts.length > 0 ? '0 4px 14px rgba(239, 68, 68, 0.3)' : '0 4px 14px rgba(16, 185, 129, 0.3)' }}>
                <BellAlertIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800">Active Alerts</h3>
            </div>
            <Link
              to="/alerts"
              className="text-sm font-medium transition-colors"
              style={{ color: 'var(--theme-600)' }}
            >
              View All →
            </Link>
          </div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <ShieldCheckIcon className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-slate-600">No active alerts</p>
                <p className="text-sm text-slate-500">All systems nominal</p>
              </div>
            ) : (
              alerts.slice(0, 5).map((alert, index) => (
                <AlertCard key={alert.id} alert={alert} index={index} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts by Severity */}
        <div className="glass-card-glow p-6 animate-slide-up stagger-4 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-orange-500 to-amber-500"></div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg" style={{ boxShadow: '0 4px 14px rgba(249, 115, 22, 0.3)' }}>
              <ExclamationTriangleIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">{isToday ? "Today's" : 'Period'} Alerts</h3>
              <p className="text-xs text-slate-500">Distribution by severity level</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-48">
              <AlertsDonutChart data={data?.alerts?.bySeverity} />
            </div>
            <div className="space-y-3">
              <SeverityRow label="Critical" value={data?.alerts?.bySeverity?.CRITICAL || 0} color="red" />
              <SeverityRow label="High" value={data?.alerts?.bySeverity?.HIGH || 0} color="orange" />
              <SeverityRow label="Medium" value={data?.alerts?.bySeverity?.MEDIUM || 0} color="yellow" />
              <SeverityRow label="Low" value={data?.alerts?.bySeverity?.LOW || 0} color="blue" />
            </div>
          </div>
        </div>

        {/* Site Status */}
        <div className="glass-card-glow p-6 animate-slide-up stagger-5 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
          <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'var(--gradient-primary)' }}></div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: 'var(--gradient-primary)', boxShadow: 'var(--shadow-primary)' }}>
              <UsersIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Site Status</h3>
              <p className="text-xs text-slate-500">Real-time compliance by location</p>
            </div>
          </div>
          <div className="space-y-3">
            {data?.sites?.list?.length > 0 ? (
              data.sites.list.map((site, index) => (
                <SiteStatusRow key={site.id} site={site} index={index} />
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>No sites configured</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Daily Stats Chart */}
      <div className="glass-card-glow p-6 animate-slide-up stagger-6 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, var(--theme-500), var(--accent-500))' }}></div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: 'var(--gradient-primary)', boxShadow: 'var(--shadow-primary)' }}>
              <CalendarDaysIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">{daysInRange}-Day Overview</h3>
              <p className="text-xs text-slate-500">Compliance and alert trends</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
              <span className="text-xs font-medium text-emerald-700">Compliance</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
              <span className="text-xs font-medium text-red-700">Alerts</span>
            </div>
          </div>
        </div>
        <DailyStatsChart data={dailyStats?.data} />
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div>
          <div className="h-8 skeleton w-64 mb-2"></div>
          <div className="h-4 skeleton w-40"></div>
        </div>
        <div className="h-12 skeleton w-40 rounded-xl"></div>
      </div>

      {/* Hero section skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-40 skeleton rounded-2xl"></div>
        <div className="h-40 skeleton rounded-2xl"></div>
      </div>

      {/* KPI cards skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="glass-card p-5 h-36 relative overflow-hidden">
            <div className="w-14 h-14 skeleton rounded-2xl mb-4"></div>
            <div className="h-4 skeleton w-24 mb-2"></div>
            <div className="h-8 skeleton w-16"></div>
          </div>
        ))}
      </div>

      {/* Charts skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-card p-6 h-80">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 skeleton rounded-xl"></div>
            <div>
              <div className="h-5 skeleton w-32 mb-1"></div>
              <div className="h-3 skeleton w-24"></div>
            </div>
          </div>
          <div className="h-56 skeleton rounded-xl"></div>
        </div>
        <div className="glass-card p-6 h-80">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 skeleton rounded-xl"></div>
            <div className="h-5 skeleton w-28"></div>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 skeleton rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, suffix = '', icon: Icon, color, subtitle, trend, pulse, delay }) {
  const animatedValue = useAnimatedCounter(typeof value === 'number' ? value : parseInt(value) || 0);

  const colors = {
    blue: { bg: 'bg-blue-50', iconBg: 'bg-blue-100', icon: 'text-blue-600', accent: 'from-blue-500 to-blue-600' },
    green: { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', icon: 'text-emerald-600', accent: 'from-emerald-500 to-emerald-600' },
    yellow: { bg: 'bg-amber-50', iconBg: 'bg-amber-100', icon: 'text-amber-600', accent: 'from-amber-500 to-amber-600' },
    red: { bg: 'bg-red-50', iconBg: 'bg-red-100', icon: 'text-red-600', accent: 'from-red-500 to-red-600' },
    purple: { bg: 'bg-purple-50', iconBg: 'bg-purple-100', icon: 'text-purple-600', accent: 'from-purple-500 to-purple-600' },
  };

  const config = colors[color];

  return (
    <div
      className={clsx(
        'metric-card group',
        pulse && 'ring-2 ring-red-200 ring-offset-2'
      )}
      style={{ animationDelay: `${delay * 0.05}s` }}
    >
      {/* Subtle accent bar */}
      <div className={clsx('absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r', config.accent)}></div>

      <div className="flex items-start justify-between">
        <div className={clsx('metric-card-icon', config.iconBg)}>
          <Icon className={clsx('w-6 h-6', config.icon)} />
        </div>
        {trend && (
          <div className={clsx(
            'flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold',
            trend === 'up' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          )}>
            {trend === 'up' ? (
              <ArrowTrendingUpIcon className="w-3.5 h-3.5" />
            ) : (
              <ArrowTrendingDownIcon className="w-3.5 h-3.5" />
            )}
            {trend === 'up' ? 'Good' : 'Low'}
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="metric-card-label">{title}</p>
        <p className="metric-card-value">
          {animatedValue}{suffix}
        </p>
        {subtitle && (
          <p className="text-xs text-slate-500 mt-1.5 font-medium">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function AlertCard({ alert, index }) {
  const severityConfig = {
    CRITICAL: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: FireIcon,
    },
    HIGH: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-700',
      icon: ExclamationTriangleIcon,
    },
    MEDIUM: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: ExclamationTriangleIcon,
    },
    LOW: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: BellAlertIcon,
    },
  };

  const config = severityConfig[alert.severity] || severityConfig.LOW;
  const AlertIcon = config.icon;

  const timeDiff = (date) => {
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 0) return 'just now';
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <Link
      to="/alerts"
      className={clsx(
        'block p-3 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-pointer',
        config.bg,
        config.border,
        alert.severity === 'CRITICAL' && 'animate-pulse-soft'
      )}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="flex items-start gap-3">
        {/* Thumbnail */}
        {(alert.thumbnailUrl || alert.snapshotUrl) ? (
          <AlertThumbnail
            imageUrl={getThumbnailUrl(alert)}
            alt={alert.type}
            size="sm"
            showPlaceholder={false}
            className="flex-shrink-0"
          />
        ) : (
          <div className={clsx('p-2 rounded-lg flex-shrink-0', config.bg)}>
            <AlertIcon className={clsx('w-4 h-4', config.text)} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className={clsx('font-medium text-sm truncate', config.text)}>
            {alert.type?.replace(/_/g, ' ') || 'Unknown'}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {alert.camera?.name || 'Unknown Camera'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={clsx('text-[10px] font-medium px-1.5 py-0.5 rounded', config.bg, config.text)}>
            {alert.severity}
          </span>
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {timeDiff(alert.detectedAt)}
          </span>
        </div>
      </div>
    </Link>
  );
}

function SeverityRow({ label, value, color }) {
  const colors = {
    red: { dot: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-100' },
    orange: { dot: 'bg-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-100' },
    yellow: { dot: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100' },
    blue: { dot: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100' },
  };

  const config = colors[color];

  return (
    <div className={clsx(
      'flex items-center justify-between p-3 rounded-xl border transition-all hover:shadow-sm',
      config.bg,
      config.border
    )}>
      <div className="flex items-center gap-3">
        <div className={clsx('w-2.5 h-2.5 rounded-full', config.dot)}></div>
        <span className="text-sm font-medium text-slate-700">{label}</span>
      </div>
      <span className={clsx('text-xl font-bold', config.text)}>{value}</span>
    </div>
  );
}

function SiteStatusRow({ site, index }) {
  const statusColor = site.camerasOnline > 0 ? 'emerald' : 'slate';

  return (
    <div
      className="p-4 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer"
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-semibold text-slate-800">{site.name}</p>
          <p className="text-sm text-slate-500">{site.occupancy || 0} workers on site</p>
        </div>
        <div className="text-right">
          <p className={clsx('text-2xl font-bold', `text-${statusColor}-600`)}>
            {site.camerasOnline || 0}
          </p>
          <p className="text-xs text-slate-500">cameras online</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{site.daysWithoutIncident || 0} days without incident</p>
        <span className={clsx(
          'px-2 py-0.5 rounded-full text-xs font-medium',
          site.camerasOnline > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
        )}>
          {site.camerasOnline > 0 ? 'Monitoring' : 'Offline'}
        </span>
      </div>
    </div>
  );
}

function ViolationsTrendChart({ data }) {
  if (!data) return <div className="h-64 skeleton rounded-xl"></div>;

  // Get current hour to show only hours up to now
  const currentHour = new Date().getHours();

  // Calculate max violations for dynamic scale
  const maxViolations = data.length > 0 ? Math.max(...data.map(d => d.violations || 0), 5) : 5;
  const yMax = Math.ceil(maxViolations * 1.2); // Add 20% headroom

  const chartData = {
    labels: data.map(d => `${d.hour}:00`),
    datasets: [
      {
        label: 'Total Violations',
        data: data.map(d => d.violations || 0),
        borderColor: 'rgb(249, 115, 22)',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 250);
          gradient.addColorStop(0, 'rgba(249, 115, 22, 0.3)');
          gradient.addColorStop(1, 'rgba(249, 115, 22, 0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: 'rgb(249, 115, 22)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(249, 115, 22)',
        pointRadius: (context) => {
          // Show points for hours with violations
          return data[context.dataIndex]?.violations > 0 ? 4 : 0;
        },
        pointHoverRadius: 6,
        borderWidth: 2,
      },
      {
        label: 'Critical/High',
        data: data.map(d => (d.criticalCount || 0) + (d.highCount || 0)),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: { size: 11 },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        callbacks: {
          afterBody: (context) => {
            const idx = context[0].dataIndex;
            const item = data[idx];
            if (item && item.violations > 0) {
              return [
                `PPE: ${item.ppeViolations || 0}`,
                `Critical: ${item.criticalCount || 0}`,
                `High: ${item.highCount || 0}`,
              ];
            }
            return [];
          },
        },
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: {
          color: 'rgba(100, 116, 139, 0.8)',
          maxTicksLimit: 12,
        },
      },
      y: {
        min: 0,
        max: yMax,
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: {
          color: 'rgba(100, 116, 139, 0.8)',
          stepSize: Math.max(1, Math.ceil(yMax / 5)),
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  // Check if there are any violations today
  const totalViolations = data.reduce((sum, d) => sum + (d.violations || 0), 0);

  if (totalViolations === 0) {
    return (
      <div className="h-64 flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
          <ShieldCheckIcon className="w-8 h-8 text-emerald-600" />
        </div>
        <p className="text-lg font-semibold text-slate-700">No Violations Today</p>
        <p className="text-sm text-slate-500 mt-1">All safety standards are being met</p>
      </div>
    );
  }

  return (
    <div className="h-64">
      <Bar data={chartData} options={options} />
    </div>
  );
}

function AlertsDonutChart({ data }) {
  if (!data) return <div className="h-full skeleton rounded-full"></div>;

  const chartData = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [
      {
        data: [
          data.CRITICAL || 0,
          data.HIGH || 0,
          data.MEDIUM || 0,
          data.LOW || 0,
        ],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(59, 130, 246, 0.8)',
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(249, 115, 22, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(59, 130, 246, 1)',
        ],
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
      },
    },
  };

  const total = (data.CRITICAL || 0) + (data.HIGH || 0) + (data.MEDIUM || 0) + (data.LOW || 0);

  return (
    <div className="relative h-full">
      <Doughnut data={chartData} options={options} />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-3xl font-bold text-slate-800">{total}</p>
          <p className="text-xs text-slate-500">Total</p>
        </div>
      </div>
    </div>
  );
}

function DailyStatsChart({ data }) {
  if (!data || data.length === 0) return <div className="h-64 skeleton rounded-xl"></div>;

  const chartData = {
    labels: data.map(d => d.date),
    datasets: [
      {
        label: 'Compliance %',
        data: data.map(d => d.ppeCompliance),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 250);
          gradient.addColorStop(0, 'rgba(34, 197, 94, 0.15)');
          gradient.addColorStop(1, 'rgba(34, 197, 94, 0)');
          return gradient;
        },
        yAxisID: 'y',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: 'rgb(34, 197, 94)',
        pointBorderColor: '#fff',
      },
      {
        label: 'Alerts',
        data: data.map(d => d.totalAlerts),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        yAxisID: 'y1',
        type: 'bar',
        borderRadius: 8,
        barThickness: 20,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: { color: 'rgba(100, 116, 139, 0.8)' },
      },
      y: {
        type: 'linear',
        position: 'left',
        min: 0,
        max: 100,
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: {
          color: 'rgba(100, 116, 139, 0.8)',
          callback: (v) => `${v}%`
        },
      },
      y1: {
        type: 'linear',
        position: 'right',
        min: 0,
        grid: { drawOnChartArea: false },
        ticks: { color: 'rgba(100, 116, 139, 0.8)' },
      },
    },
  };

  return (
    <div className="h-64">
      <Line data={chartData} options={options} />
    </div>
  );
}
